import { useGraphQLMutation } from '@opengov/bnp-core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { STATE_PUBLISHED, DATE_FORMAT_WITH_TIMEZONE } from '@opengov/stories-shared/constants';
import { IPublication, IStory } from '@opengov/stories-shared/types';
import moment from 'moment';
import { useCallback } from 'react';

import { graphql } from '../../../../../gql-generated/hasura';
import {
  UpdatePublicationCustomCssCodeForCustomCssMutation,
  UpdatePublicationCustomCssCodeForCustomCssMutationVariables,
  UpdateStoryCustomCssCodeForCustomCssMutation,
  UpdateStoryCustomCssCodeForCustomCssMutationVariables,
  UpdatePublicationUpdatedAtForCustomCssMutation,
  UpdatePublicationUpdatedAtForCustomCssMutationVariables
} from '../../../../../gql-generated/hasura/graphql';
import { HASURA_SERVICE_URL } from '../../../../../config/config';
import { postJson } from '../../../../utils/fetchUtils';
import { getUserById, shareOnlinePublication } from '../../../../routes/hasura';
import { useToast } from '../../../Toast/context/useToast.context';
import { publicationViewQueryKeys } from '../../PublicationView/hooks/publicationViewQueryKeys';

import {
  publicationCustomCssQueryKeys,
  PublicationWithSectionsKeyParams,
  SaveCustomCssMutationKeyParams
} from './publicationCustomCssQueryKeys';
import { invalidateStoryCssCaches } from './storyCacheInvalidation';

export type SaveCustomCssCodeParams = {
  entityId: number;
  entitySubdomain: string;
  entityUuid: string;
  userId: number;
  publicationId: string;
  publication?: IPublication & { customCssCode: string };
  story?: IStory & { customCssCode: string };
  enableGoogleTranslateForPublications?: boolean;
};

export type SaveCustomCssMessages = {
  errorTitle: string;
  errorBody: string;
  successTitle: string;
  successBody: string;
};

const UpdatePublicationCustomCssCodeForCustomCssDocument = graphql(`
  mutation UpdatePublicationCustomCssCodeForCustomCss($id: uuid!, $customCssCode: String) {
    publication: update_publications_by_pk(pk_columns: { id: $id }, _set: { customCssCode: $customCssCode }) {
      id
      customCssCode
    }
  }
`);

const UpdateStoryCustomCssCodeForCustomCssDocument = graphql(`
  mutation UpdateStoryCustomCssCodeForCustomCss($id: String!, $customCssCode: String!) {
    story: update_stories_by_pk(pk_columns: { _id: $id }, _set: { customCssCode: $customCssCode }) {
      _id
      customCssCode
    }
  }
`);

const UpdatePublicationUpdatedAtForCustomCssDocument = graphql(`
  mutation UpdatePublicationUpdatedAtForCustomCss(
    $publicationId: uuid!
    $updatedAt: timestamptz!
    $userId: String!
  ) {
    publication: update_publications_by_pk(
      pk_columns: { id: $publicationId }
      _set: { updatedAt: $updatedAt, updatedBy: $userId }
    ) {
      id
      updatedAt
    }
  }
`);

type RepublishPublicationStoriesParams = {
  publicationId: string;
  entityId: number;
  entityUuid: string;
};

type RepublishStoryParams = {
  storyId: string;
  publicationId: string;
  entityId: number;
  entityUuid: string;
};

type SetPublicationThumbnailParams = {
  publicationId: string;
  entityId: number;
  entitySubdomain: string;
};

export function useSaveCustomCssCode(
  mutationKeyParams: SaveCustomCssMutationKeyParams,
  messages: SaveCustomCssMessages
) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  const updatePublicationCssMutation = useGraphQLMutation<
    UpdatePublicationCustomCssCodeForCustomCssMutation,
    UpdatePublicationCustomCssCodeForCustomCssMutationVariables
  >({
    mutationKey: [...publicationCustomCssQueryKeys.all, 'updatePublicationCss'],
    document: UpdatePublicationCustomCssCodeForCustomCssDocument,
    url: HASURA_SERVICE_URL
  });

  const updateStoryCssMutation = useGraphQLMutation<
    UpdateStoryCustomCssCodeForCustomCssMutation,
    UpdateStoryCustomCssCodeForCustomCssMutationVariables
  >({
    mutationKey: [...publicationCustomCssQueryKeys.all, 'updateStoryCss'],
    document: UpdateStoryCustomCssCodeForCustomCssDocument,
    url: HASURA_SERVICE_URL
  });

  // Bumps publication.updatedAt directly via Hasura instead of routing
  // through `publicationViewEffects.updatePublicationUpdatedAt` so this
  // hook stays compose-app-free.
  const updatePublicationUpdatedAtMutation = useGraphQLMutation<
    UpdatePublicationUpdatedAtForCustomCssMutation,
    UpdatePublicationUpdatedAtForCustomCssMutationVariables
  >({
    mutationKey: [...publicationCustomCssQueryKeys.all, 'updatePublicationUpdatedAt'],
    document: UpdatePublicationUpdatedAtForCustomCssDocument,
    url: HASURA_SERVICE_URL
  });

  // Stories REST endpoints use cookie session auth — bnp-core's Hasura
  // helpers send Auth0 Bearer tokens which the Stories service rejects
  // with 401, so we use plain `postJson` here.
  const republishPublicationStoriesMutation = useMutation<unknown, Error, RepublishPublicationStoriesParams>({
    mutationKey: [...publicationCustomCssQueryKeys.all, 'republishPublicationStories'],
    mutationFn: ({ publicationId, entityId, entityUuid }) =>
      postJson(`/api/stories/v1/publications/${publicationId}/republish-stories`, {
        entityId,
        entityUuid
      })
  });

  const republishStoryMutation = useMutation<unknown, Error, RepublishStoryParams>({
    mutationKey: [...publicationCustomCssQueryKeys.all, 'republishStory'],
    mutationFn: ({ storyId, publicationId, entityId, entityUuid }) =>
      postJson(`/api/stories/v1/stories/${storyId}/republish`, {
        entityId,
        publicationId,
        entityUuid
      })
  });

  // Refreshes the publication thumbnail directly instead of routing
  // through `publicationViewEffects.setPublicationThumbnail` so this
  // hook stays compose-app-free.
  const setPublicationThumbnailMutation = useMutation<unknown, Error, SetPublicationThumbnailParams>({
    mutationKey: [...publicationCustomCssQueryKeys.all, 'setPublicationThumbnail'],
    mutationFn: ({ publicationId, entityId, entitySubdomain }) =>
      postJson(`/api/stories/v1/publications/${publicationId}/updateThumbnail`, {
        entityId,
        entitySubdomain,
        publicationId
      })
  });

  // TanStack Query v4 names this `isLoading`; v5 renames it `isPending`.
  const isLoading =
    updatePublicationCssMutation.isLoading ||
    updateStoryCssMutation.isLoading ||
    updatePublicationUpdatedAtMutation.isLoading ||
    republishPublicationStoriesMutation.isLoading ||
    republishStoryMutation.isLoading ||
    setPublicationThumbnailMutation.isLoading;

  const saveCustomCssCode = useCallback(
    async (params: SaveCustomCssCodeParams): Promise<void> => {
      const {
        entityId,
        entitySubdomain,
        entityUuid,
        userId,
        publicationId,
        publication,
        story,
        enableGoogleTranslateForPublications
      } = params;

      try {
        const cssUpdates: Promise<unknown>[] = [];
        if (publication) {
          cssUpdates.push(
            updatePublicationCssMutation.mutateAsync({
              id: publicationId,
              customCssCode: publication.customCssCode
            })
          );
        }
        if (story) {
          cssUpdates.push(
            updateStoryCssMutation.mutateAsync({
              id: story._id,
              customCssCode: story.customCssCode
            })
          );
        }
        await Promise.all(cssUpdates);

        const republishWork: Promise<unknown>[] = [];

        if (publication) {
          republishWork.push(
            republishPublicationStoriesMutation
              .mutateAsync({ publicationId, entityId, entityUuid })
              .then(() =>
                setPublicationThumbnailMutation.mutateAsync({
                  publicationId,
                  entityId,
                  entitySubdomain
                })
              )
          );

          if (publication.status === STATE_PUBLISHED && publication.publicUrl) {
            // publicUrl ends with the published id: `.../<publishedId>`.
            const publishedId = publication.publicUrl.split('/').pop() ?? '';
            republishWork.push(
              shareOnlinePublication({
                publicationId,
                userId,
                publishedId,
                entitySubdomain,
                enableGoogleTranslateForPublications: enableGoogleTranslateForPublications ?? false
              })
            );
          }
        } else if (story) {
          republishWork.push(
            republishStoryMutation
              .mutateAsync({
                storyId: story._id,
                publicationId,
                entityId,
                entityUuid
              })
              .then(() =>
                setPublicationThumbnailMutation.mutateAsync({
                  publicationId,
                  entityId,
                  entitySubdomain
                })
              )
          );
        }

        const authorId = await getUserById(userId);
        republishWork.push(
          updatePublicationUpdatedAtMutation.mutateAsync({
            publicationId,
            updatedAt: moment(new Date()).format(DATE_FORMAT_WITH_TIMEZONE),
            userId: authorId
          })
        );

        await Promise.all(republishWork);

        // Optimistic cache updates write the raw server shape so each
        // read hook's `select()` runs against the structure it expects
        // — writing the normalized UI shape would make `select()` return
        // null and wipe the just-saved values from the editor.
        const queryKeyParams: PublicationWithSectionsKeyParams = {
          publicationId,
          entityId
        };
        if (publication) {
          queryClient.setQueryData(
            publicationCustomCssQueryKeys.publicationWithSections(queryKeyParams),
            (old: { publication: { customCssCode?: string | null } | null } | undefined) => {
              if (!old || !old.publication) return old;
              return {
                ...old,
                publication: { ...old.publication, customCssCode: publication.customCssCode }
              };
            }
          );
        }
        if (story) {
          queryClient.setQueryData(
            publicationCustomCssQueryKeys.storyWithContent(story._id),
            (old: { stories: ({ customCssCode?: string | null } | null)[] } | undefined) => {
              if (!old || !old.stories?.[0]) return old;
              const [first, ...rest] = old.stories;
              return {
                ...old,
                stories: [{ ...first, customCssCode: story.customCssCode }, ...rest]
              };
            }
          );
        }

        // Refetch the caches the story view builds its iframe from. Awaited on
        // purpose: the iframe's CSS is written from `initialContent` only at
        // mount, so if the refetch were still in flight when the user navigated
        // to the story, the frame would mount from the stale cache and ignore
        // the CSS that landed a moment later. Awaiting costs one round trip and
        // makes the success toast mean "the story view will render this".
        await invalidateStoryCssCaches(
          queryClient,
          // A publication CSS change cascades to every story under it; a
          // story-only change is scoped to that story.
          publication ? 'all' : story ? [story._id] : []
        );

        if (publication) {
          // `StoryContentsFrame` prefers `publication.customCssCode` from this
          // cache over the copy nested in the story document, so a stale entry
          // here keeps serving the old publication CSS even after the story
          // itself refetched.
          await queryClient.invalidateQueries({
            queryKey: publicationViewQueryKeys.publication({ publicationId }),
            refetchType: 'all'
          });
        }

        toggleToast({
          title: messages.successTitle,
          body: messages.successBody,
          severity: 'success'
        });
      } catch {
        toggleToast({
          title: messages.errorTitle,
          body: messages.errorBody,
          severity: 'error'
        });
      }
    },
    [
      messages.errorTitle,
      messages.errorBody,
      messages.successTitle,
      messages.successBody,
      queryClient,
      republishPublicationStoriesMutation,
      republishStoryMutation,
      setPublicationThumbnailMutation,
      toggleToast,
      updatePublicationCssMutation,
      updatePublicationUpdatedAtMutation,
      updateStoryCssMutation
    ]
  );

  return {
    saveCustomCssCode,
    isLoading,
    mutationKey: publicationCustomCssQueryKeys.saveCss(mutationKeyParams)
  };
}
